import Card from "creditcards/card";
import Cvc from "creditcards/cvc";

/* Default card scheme validation rules from creditcards-types package */
import visa from "creditcards-types/types/visa";
import mastercard from "creditcards-types/types/mastercard";
import maestro from "creditcards-types/types/maestro";
import americanExpress from "creditcards-types/types/american-express";
import dinersClub from "creditcards-types/types/diners-club";
import jcb from "creditcards-types/types/jcb";
import mada from "creditcards-types/types/mada";

/* Customised card scheme validation rules */
import discover from "../../constants/schemeValidationRules/discover";
import unionpay from "../../constants/schemeValidationRules/unionpay";

import { compose } from "../../utils";
import { removeNonDigits, replaceFullwidthChars } from "../../utils/string";

/*
 * NOTE: There is security concern over how
 * creditcards-types package is manually published by the maintainer,
 * since the published package could have been tampered with.
 * So instead of further contributing to the package,
 * we will fix the package to the last version
 * we've used in Production and observed no abnormal behaviour.
 * Any further changes to card scheme validation rules
 * will be made as extensions of existing rules in this package.
 */
const acceptedSchemeValidationRules = [
  // Mada needs to come before Visa and Mastercard,
  // otherwise it will get overridden
  mada,
  visa,
  mastercard,
  americanExpress,
  dinersClub,
  discover,
  jcb,
  unionpay,
  // Maestro needs to come last
  // because it covers all remaining 6 BINs
  maestro,
];

const cardNumberValidator = Card(acceptedSchemeValidationRules);

const cvvValidator = Cvc(acceptedSchemeValidationRules);

const isCardValid = (number, type) => {
  return cardNumberValidator.isValid(number, type);
};

function formatCard(cardNumber) {
  const newValue = compose(removeNonDigits, replaceFullwidthChars)(cardNumber);

  const scheme = getCardScheme(newValue);

  return scheme ? cardNumberValidator.format(newValue) : newValue.slice(0, 19);
}

function getCardScheme(cardNumber) {
  return cardNumberValidator.type(cardNumber, true);
}

const isCvvValid = (cvv, scheme) =>
  isMaestroAndEmptyCvv(cvv, scheme) || cvvValidator.isValid(cvv, scheme);

const isMaestroAndEmptyCvv = (cvv, scheme) => !cvv && scheme === "Maestro";

const getCardBIN = (cardNumber) => {
  return cardNumber && cardNumber.length >= 8 ? cardNumber.slice(0, 8) : "";
};

export { formatCard, getCardScheme, isCardValid, isCvvValid, getCardBIN };
